import { groupBy, isNil } from 'lodash'
import type { Moment } from 'moment'

import { dateToString } from 'modules/core/lib/dates'
import { amountToNumber, roundTo } from 'modules/core/lib/formatCurrencyUtils'

import type { SupplementType } from 'modules/payroll_bundle/components/common/helper/worksheet'
import type { ImportData } from 'modules/payroll_bundle/lib/bulkImportSupplementsContext'
import { amountInCent } from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/amountInCents'

import filterSelectedValidRows from './filterSelectedValidRows'

function totalBySupplementType(rows: ImportData[], supplementTypes: SupplementType[]) {
  return supplementTypes.reduce((supplementTypeIdToTotalAmount, supplementType) => {
    const supplementTypeTotal = rows.reduce(
      (total, row) => total + amountToNumber(row[supplementType.id]),
      0
    )
    return { ...supplementTypeIdToTotalAmount, [supplementType.id]: supplementTypeTotal }
  }, {})
}

function buildEmployeeSupplements({
  employeeId,
  legalEntityId,
  supplementTypeIdToTotalAmount,
  supplementTypes,
  policyPeriodId,
  effectiveOn,
}: {
  employeeId: string
  legalEntityId: string
  supplementTypeIdToTotalAmount: Record<string, number>
  supplementTypes: SupplementType[]
  policyPeriodId: string
  effectiveOn: Moment
}) {
  return Object.entries(supplementTypeIdToTotalAmount).flatMap(([supplementTypeId, amount]) => {
    if (amount === 0) {
      // `flatMap` will remove these entries
      // (first approach was `map` and then `filter` out the nulls, but then
      // TypeScript complains down the line, https://stackoverflow.com/a/59726888 )
      return []
    }

    const supplementType = supplementTypes.find((st) => st.id === supplementTypeId)
    return {
      amount_in_cents: amountInCent(roundTo(amount)),
      effective_on: dateToString(effectiveOn, 'backend'),
      contracts_taxonomy_id: supplementType?.legalEntityIdToTaxonomyId[legalEntityId],
      payroll_policy_period_id: policyPeriodId,
      unit: supplementType?.units || 'money',
      employee_id: employeeId,
    }
  })
}

export default function buildPayloadForSupplementBulkImport({
  filename,
  authorId,
  importedSupplements,
  selectedSupplements,
  supplementTypes,
  policyPeriodId,
  effectiveOn,
}: {
  filename: string
  authorId: string
  importedSupplements: ImportData[]
  selectedSupplements: Set<string>
  supplementTypes: SupplementType[]
  policyPeriodId: string
  effectiveOn: Moment
}) {
  const selectedValidRows = filterSelectedValidRows({
    rows: importedSupplements,
    selectedSupplements,
    supplementTypes,
  })

  const employeeIdToRows = groupBy(selectedValidRows, (row) => row.employee_id)

  const supplements = Object.values(employeeIdToRows).flatMap((rows) => {
    if (!rows[0]) return []

    const supplementTypeIdToTotalAmount = totalBySupplementType(rows, supplementTypes)

    return buildEmployeeSupplements({
      employeeId: rows[0].employee_id,
      legalEntityId: rows[0].legal_entity_id,
      supplementTypeIdToTotalAmount,
      supplementTypes,
      policyPeriodId,
      effectiveOn,
    })
  })

  return {
    filename: filename,
    policy_period_id: policyPeriodId,
    author_id: authorId,
    supplements: supplements.filter((s) => !isNil(s.amount_in_cents)),
  }
}
